import { Navigate, useParams } from 'react-router-dom';

import { LoadingState } from '../../components/loading-state';
import { useGetEncounterDetailsQuery } from '../../store/api';
import { resourceDetailPath } from '../../utils/resource-routes';

/**
 * Forwards the pre-consolidation encounter URLs to the canonical one (GDN-2723).
 *
 * A redirect, not a view — the encounter renders in exactly one place now. This
 * exists because admin pages cross-link to encounters by bare id (telemetry
 * cards, streaming threads, note-processing sessions, note and transcript
 * details), and most of those callers don't have the organization the
 * canonical URL needs. Resolving it from the record here is far less invasive
 * than threading an org through every caller, and those links can be pointed
 * straight at the canonical URL as their own views migrate.
 */
export default function EncounterRedirect() {
  const { encounterId: scoped, id: legacy } = useParams<{
    encounterId?: string;
    id?: string;
  }>();
  const encounterId = scoped || legacy || '';

  const { data, isLoading } = useGetEncounterDetailsQuery(
    { encounterId },
    { skip: !encounterId },
  );

  if (!encounterId) return <Navigate to="/admin-v2" replace />;
  if (isLoading) return <LoadingState message="Opening encounter..." />;

  const organizationId = data?.encounter?.organization_id;
  if (!organizationId) {
    // No org means no canonical address — the record is gone, or it predates
    // tenanting. Search is the honest destination.
    return <Navigate to="/admin-v2" replace />;
  }

  return (
    <Navigate
      to={resourceDetailPath({ organizationId }, 'encounter', encounterId)}
      replace
    />
  );
}
